import { createContext, useContext, useMemo, useState, useCallback, ReactNode } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { makeTheme, ColorMode } from './makeTheme';

const STORAGE_KEY = 'tr-color-mode';

interface ColorModeContextValue {
    mode: ColorMode;
    toggleColorMode: () => void;
    setColorMode: (mode: ColorMode) => void;
}

const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined);

function readInitialMode(): ColorMode {
    if (typeof window === 'undefined') return 'dark';
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'dark'; // default: faithful dark
}

/**
 * Owns the active color mode, persists it to localStorage, and supplies the
 * matching MUI theme + CssBaseline to the whole app. Dark is the default;
 * users can switch to light via the ColorModeToggle.
 */
export function ColorModeProvider({ children }: { children: ReactNode }) {
    const [mode, setMode] = useState<ColorMode>(readInitialMode);

    const setColorMode = useCallback((next: ColorMode) => {
        setMode(next);
        try {
            window.localStorage.setItem(STORAGE_KEY, next);
        } catch {
            /* storage unavailable (private mode) — keep in-memory only */
        }
    }, []);

    const toggleColorMode = useCallback(() => {
        setMode(prev => {
            const next: ColorMode = prev === 'dark' ? 'light' : 'dark';
            try {
                window.localStorage.setItem(STORAGE_KEY, next);
            } catch {
                /* ignore */
            }
            return next;
        });
    }, []);

    const theme = useMemo(() => makeTheme(mode), [mode]);
    const ctx = useMemo(
        () => ({ mode, toggleColorMode, setColorMode }),
        [mode, toggleColorMode, setColorMode],
    );

    return (
        <ColorModeContext.Provider value={ctx}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
}

export function useColorMode(): ColorModeContextValue {
    const ctx = useContext(ColorModeContext);
    if (!ctx) {
        throw new Error('useColorMode must be used within a ColorModeProvider');
    }
    return ctx;
}
